import type { AvailabilityMap } from "./book";
import { captureAppError } from "../observability/sentry";

const TTL_MS = 5 * 60 * 1000; // 5 min — slots change as bookings are made
const STORAGE_PREFIX = "hh_avail_v2_";
const LEGACY_STORAGE_PREFIX = "hh_avail_";

type CachedAvail = {
  slots: AvailabilityMap;
  bookedSlots: AvailabilityMap;
  timestamp: number;
};

const memCache = new Map<string, CachedAvail>();

// FE-1: include userId in cache key so different users never share availability data
function memKey(userId: string, serviceId: number, numUnits?: number, postal?: string) {
  const serviceSuffix = numUnits != null ? `${serviceId}_${numUnits}` : String(serviceId);
  const postalSuffix = postal ? `_postal_${postal}` : "";
  const suffix = `${serviceSuffix}${postalSuffix}`;
  return `${userId}:${suffix}`;
}

function storageKey(userId: string, serviceId: number, numUnits?: number, postal?: string) {
  return STORAGE_PREFIX + memKey(userId, serviceId, numUnits, postal);
}

function isCachedAvail(value: unknown): value is CachedAvail {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.timestamp === "number" &&
    v.slots !== null &&
    typeof v.slots === "object" &&
    v.bookedSlots !== null &&
    typeof v.bookedSlots === "object"
  );
}

function readStorage(key: string): CachedAvail | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    // FE-10: runtime guard — reject malformed cache entries
    return isCachedAvail(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeStorage(key: string, entry: CachedAvail) {
  try {
    localStorage.setItem(key, JSON.stringify(entry));
  } catch (err) {
    // FE-11: report quota errors; silently drop non-quota failures
    if (err instanceof DOMException && err.name === "QuotaExceededError") {
      captureAppError(err, { area: "availabilityCache", action: "writeStorage" });
    }
  }
}

/**
 * Returns any cached availability regardless of age.
 * Used as a stale fallback when a fresh network fetch fails.
 */
export function getStaleAvailability(
  userId: string,
  serviceId: number,
  numUnits?: number,
  postal?: string,
): CachedAvail | null {
  const k = memKey(userId, serviceId, numUnits, postal);
  const mem = memCache.get(k);
  if (mem) return mem;
  const stored = readStorage(storageKey(userId, serviceId, numUnits, postal));
  if (stored) {
    memCache.set(k, stored);
    return stored;
  }
  return null;
}

/** Saves a successful fetch result to both cache tiers. */
export function setCachedAvailability(
  userId: string,
  serviceId: number,
  slots: AvailabilityMap,
  bookedSlots: AvailabilityMap,
  numUnits?: number,
  postal?: string,
): void {
  const entry: CachedAvail = { slots, bookedSlots, timestamp: Date.now() };
  const k = memKey(userId, serviceId, numUnits, postal);
  memCache.set(k, entry);
  writeStorage(storageKey(userId, serviceId, numUnits, postal), entry);
}

/** True when the cached entry is younger than TTL_MS. */
export function getFreshAvailability(
  userId: string,
  serviceId: number,
  numUnits?: number,
  postal?: string,
): CachedAvail | null {
  const cached = getStaleAvailability(userId, serviceId, numUnits, postal);
  return cached && Date.now() - cached.timestamp < TTL_MS ? cached : null;
}

/** True when the cached entry is younger than TTL_MS. */
export function isFreshAvailability(
  userId: string,
  serviceId: number,
  numUnits?: number,
  postal?: string,
): boolean {
  return getFreshAvailability(userId, serviceId, numUnits, postal) !== null;
}

// FE-2: clear all availability entries so post-booking refreshes start fresh
export function invalidateAvailabilityCache(): void {
  memCache.clear();
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (
        k?.startsWith(STORAGE_PREFIX) ||
        k?.startsWith(LEGACY_STORAGE_PREFIX)
      ) {
        keysToRemove.push(k);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // storage may be unavailable in some environments
  }
}
